import { useContext, useEffect, useState } from 'react'

import { formatCurrency } from 'utils/currency'
import { AuthContext } from 'contexts/AuthContext'
import { Wrapper } from 'components'
import { AppContext } from 'contexts/AppContext'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  Input,
  Label,
  Select,
  SelectAction,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import {
  ArrowDown,
  ArrowsDownUp,
  ArrowUp,
  MagnifyingGlass,
  MicrosoftExcelLogo,
} from 'phosphor-react'
import { renderBadge } from 'utils/renderBadge'
import { RouteNamesAuth } from 'routes/enum'
import { policiesService } from 'api/services/policies'
import {
  GetPolicyListParams,
  PolicyListItem,
  PolicyListResponse,
} from 'api/types/policies'
import { renderPolicyStatus } from 'utils/renderPolicyStatus'

type SortProp =
  | 'Nº da Apólice'
  | 'Produto'
  | 'Tomador'
  | 'Prémio Anual'
  | 'Data da Renovação'
  | 'Estado'
  | 'Branch'
  | 'Razão'

export const Policies = () => {
  const navigate = useNavigate()
  const { currentBroker, accessLevel } = useContext(AuthContext)
  const { products } = useContext(AppContext)
  const [tableData, setTableData] = useState<PolicyListItem[] | null>(null)
  const [sortProp, setSortProp] = useState<SortProp | null>(null)
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number | null>(null)

  const brokerNo = currentBroker?.BrokerNo ? currentBroker?.BrokerNo : 0

  const [queryVars, setQueryVars] = useState<GetPolicyListParams>({
    brokerNo: brokerNo,
    product: undefined,
    status: undefined,
    policyNo: undefined,
    nif: undefined,
    page: 0,
  })

  const fetchPolicies = async () => {
    if (brokerNo === 0) return false

    try {
      setTableData(null)
      setIsLoading(true)
      const response = (await policiesService.getPoliciesList({
        ...queryVars,
      })) as PolicyListResponse

      setTableData(response.Policies)
      setTotalCount(response.TotalCount ?? null)

      setIsLoading(false)
    } catch (error) {
      setTableData(null)
      setTotalCount(null)
      setIsLoading(false)
    }
  }

  const handleSort = (prop: SortProp) => {
    setSortProp(prev => {
      if (prev === prop) {
        if (sortOrder === 'ASC') {
          setSortOrder('DESC')
        } else {
          setSortOrder('ASC')
        }
      } else {
        setSortOrder('ASC')
      }
      return prop
    })
  }

  const handleSearch = async () => {
    setQueryVars(prev => ({ ...prev, brokerNo: brokerNo }))
    setIsLoading(true)
    fetchPolicies()
  }

  const handleExcelDownload = async () => {
    try {
      const response = (await policiesService.getPoliciesList({
        ...queryVars,
        output: 'Excel',
      })) as PolicyListResponse

      const contentType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

      // decode base64 in chunks (handles large files)
      const byteChars = atob(response.Output || '')
      const sliceSize = 1024
      const byteArrays = []
      for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
        const slice = byteChars.slice(offset, offset + sliceSize)
        const byteNumbers = new Array(slice.length)
        for (let i = 0; i < slice.length; i++)
          byteNumbers[i] = slice.charCodeAt(i)
        byteArrays.push(new Uint8Array(byteNumbers))
      }

      const blob = new Blob(byteArrays, { type: contentType })
      const url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = Date.now().toString() // respected by most modern browsers
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading Excel file:', error)
      toast.error(
        'Erro ao descarregar o ficheiro Excel. Por favor, tente novamente.',
      )
    }
  }

  useEffect(() => {
    setQueryVars(prev => ({ ...prev, brokerNo: brokerNo }))
  }, [brokerNo])

  useEffect(() => {
    fetchPolicies()
  }, [queryVars.page, queryVars.brokerNo])

  useEffect(() => {
    if (!tableData || !sortProp) return

    const sorted = [...tableData].sort((a, b) => {
      const aValue = a[sortProp]
      const bValue = b[sortProp]

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'ASC' ? aValue - bValue : bValue - aValue
      }
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'ASC'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }
      return 0
    })

    setTableData(sorted)
  }, [sortProp, sortOrder])

  useEffect(() => {
    if (accessLevel !== null && accessLevel < 1) {
      navigate(RouteNamesAuth.NO_PERMISSION)
    }
  }, [accessLevel, navigate])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <h2 className="uppercase text-sm font-medium text-metal-900">
          Consulte as suas apólices
        </h2>
        <h1
          className={`pb-10 text-xxxl font-medium ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'}`}
        >
          Apólices
        </h1>
        <div className="lg:py-8">
          <Card className="w-full max-w-full p-4 md:p-6">
            <div className="grid grid-col-1 sm:grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 w-full items-end">
              <div>
                <Label className="text-body-4 block pb-2">Produto</Label>
                <Select
                  name="product"
                  onValueChange={val => {
                    if (val === 'none') {
                      setQueryVars(prev => ({ ...prev, product: undefined }))
                      return
                    }
                    setQueryVars(prev => ({ ...prev, product: val }))
                  }}
                >
                  <SelectAction className="[&_span]:truncate [&_span]:max-w-[90%]">
                    <SelectValue placeholder="Produto" />
                  </SelectAction>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="none">Todos</SelectItem>
                      {products.map(product => (
                        <SelectItem
                          key={product.product}
                          value={product.product}
                        >
                          {product.description}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-body-4 block pb-2">NIF</Label>
                <Input
                  placeholder="NIF"
                  value={queryVars.nif ?? ''}
                  type="text"
                  onChange={e => {
                    const val = e.target.value
                    setQueryVars(prev => ({
                      ...prev,
                      nif: val === '' ? undefined : val,
                    }))
                  }}
                />
              </div>

              <div>
                <Label className="text-body-4 block pb-2">Nº Apólice</Label>
                <Input
                  placeholder="Nº Apólice"
                  value={queryVars.policyNo ?? ''}
                  type="text"
                  onChange={e => {
                    const val = e.target.value
                    setQueryVars(prev => ({
                      ...prev,
                      policyNo: val === '' ? undefined : Number(val),
                    }))
                  }}
                />
              </div>

              <div>
                <Label className="text-body-4 block pb-2">Estado</Label>
                <Select
                  name="status"
                  onValueChange={val => {
                    if (val === 'none') {
                      setQueryVars(prev => ({ ...prev, status: undefined }))
                      return
                    }
                    setQueryVars(prev => ({
                      ...prev,
                      status: val as GetPolicyListParams['status'],
                    }))
                  }}
                >
                  <SelectAction className="[&_span]:truncate [&_span]:max-w-[90%]">
                    <SelectValue placeholder="Estado" />
                  </SelectAction>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="none">Todos</SelectItem>
                      {[
                        { label: 'Ativas', value: 'ACTIVE' },
                        { label: 'Anuladas', value: 'CANCEL' },
                      ].map(e => (
                        <SelectItem key={e.value} value={e.value}>
                          {e.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-4">
                <Button
                  color="primary"
                  variant="default"
                  className={`h-11 gap-2 text-body-5 ${currentBroker?.IsPrimePartner ? 'bg-prime hover:bg-black' : 'bg-primary-600'}`}
                  onClick={() => handleSearch()}
                >
                  <MagnifyingGlass size={20} />
                  Pesquisar
                </Button>
                <Button
                  color="secondary"
                  variant="outline"
                  className="h-11 gap-2 text-body-5"
                  onClick={() => handleExcelDownload()}
                >
                  <MicrosoftExcelLogo size={20} />
                  Excel
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : !tableData ? (
          <Card className="p-4 w-full max-w-full">
            <div className="text-heading-6 text-metal-400">
              Apólices indisponíveis
            </div>
          </Card>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <div className="flex items-center gap-1 whitespace-nowrap">
                      Nº da Apólice
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Nº da Apólice'
                            ? 'text-primary-600'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Nº da Apólice')}
                      >
                        {sortProp === 'Nº da Apólice' &&
                          sortOrder === 'ASC' && <ArrowUp size={16} />}
                        {sortProp === 'Nº da Apólice' &&
                          sortOrder === 'DESC' && <ArrowDown size={16} />}
                        {sortProp !== 'Nº da Apólice' && (
                          <ArrowsDownUp size={16} />
                        )}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      Produto
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Produto'
                            ? 'text-primary'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Produto')}
                      >
                        {sortProp === 'Produto' && sortOrder === 'ASC' && (
                          <ArrowUp size={16} />
                        )}
                        {sortProp === 'Produto' && sortOrder === 'DESC' && (
                          <ArrowDown size={16} />
                        )}
                        {sortProp !== 'Produto' && <ArrowsDownUp size={16} />}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      Tomador
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Tomador'
                            ? 'text-primary'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Tomador')}
                      >
                        {sortProp === 'Tomador' && sortOrder === 'ASC' && (
                          <ArrowUp size={16} />
                        )}
                        {sortProp === 'Tomador' && sortOrder === 'DESC' && (
                          <ArrowDown size={16} />
                        )}
                        {sortProp !== 'Tomador' && <ArrowsDownUp size={16} />}
                      </Button>
                    </div>
                  </TableHead>
                  {(accessLevel === 4 || accessLevel === 5) && (
                    <TableHead>
                      <div className="flex items-center gap-1">
                        Broker
                        <Button
                          variant="link"
                          className={`${
                            sortProp === 'Branch'
                              ? 'text-primary'
                              : 'text-metal-400'
                          } px-0`}
                          onClick={() => handleSort('Branch')}
                        >
                          {sortProp === 'Branch' && sortOrder === 'ASC' && (
                            <ArrowUp size={16} />
                          )}
                          {sortProp === 'Branch' && sortOrder === 'DESC' && (
                            <ArrowDown size={16} />
                          )}
                          {sortProp !== 'Branch' && <ArrowsDownUp size={16} />}
                        </Button>
                      </div>
                    </TableHead>
                  )}
                  <TableHead>
                    <div className="flex items-center gap-1">
                      Prémio Anual
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Prémio Anual'
                            ? 'text-primary'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Prémio Anual')}
                      >
                        {sortProp === 'Prémio Anual' && sortOrder === 'ASC' && (
                          <ArrowUp size={16} />
                        )}
                        {sortProp === 'Prémio Anual' &&
                          sortOrder === 'DESC' && <ArrowDown size={16} />}
                        {sortProp !== 'Prémio Anual' && (
                          <ArrowsDownUp size={16} />
                        )}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      Data da Renovação
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Data da Renovação'
                            ? 'text-primary'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Data da Renovação')}
                      >
                        {sortProp === 'Data da Renovação' &&
                          sortOrder === 'ASC' && <ArrowUp size={16} />}
                        {sortProp === 'Data da Renovação' &&
                          sortOrder === 'DESC' && <ArrowDown size={16} />}
                        {sortProp !== 'Data da Renovação' && (
                          <ArrowsDownUp size={16} />
                        )}
                      </Button>
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      Estado
                      <Button
                        variant="link"
                        className={`${
                          sortProp === 'Estado'
                            ? 'text-primary'
                            : 'text-metal-400'
                        } px-0`}
                        onClick={() => handleSort('Estado')}
                      >
                        {sortProp === 'Estado' && sortOrder === 'ASC' && (
                          <ArrowUp size={16} />
                        )}
                        {sortProp === 'Estado' && sortOrder === 'DESC' && (
                          <ArrowDown size={16} />
                        )}
                        {sortProp !== 'Estado' && <ArrowsDownUp size={16} />}
                      </Button>
                    </div>
                  </TableHead>
                  {queryVars.status === 'CANCEL' && (
                    <TableHead>
                      <div className="flex items-center gap-1">
                        Razão
                        <Button
                          variant="link"
                          className={`${
                            sortProp === 'Razão'
                              ? 'text-primary'
                              : 'text-metal-400'
                          } px-0`}
                          onClick={() => handleSort('Razão')}
                        >
                          {sortProp === 'Razão' && sortOrder === 'ASC' && (
                            <ArrowUp size={16} />
                          )}
                          {sortProp === 'Razão' && sortOrder === 'DESC' && (
                            <ArrowDown size={16} />
                          )}
                          {sortProp !== 'Razão' && <ArrowsDownUp size={16} />}
                        </Button>
                      </div>
                    </TableHead>
                  )}
                  <TableHead>
                    <div></div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableData &&
                  tableData.map((policy: any, index: number) => {
                    return (
                      <TableRow
                        key={`${policy.PolicyNo}-${index}`}
                        className="even:bg-primary-50/25"
                      >
                        <TableCell>{policy['Nº da Apólice']}</TableCell>
                        <TableCell>{policy['Produto']}</TableCell>
                        <TableCell>{policy['Tomador']}</TableCell>
                        {(accessLevel === 4 || accessLevel === 5) && (
                          <TableCell>{policy['Branch']}</TableCell>
                        )}
                        <TableCell className="text-right">
                          {formatCurrency(policy['Prémio Anual'])}
                        </TableCell>
                        <TableCell>{policy['Data da Renovação']}</TableCell>
                        <TableCell>{renderBadge(policy['Estado'])}</TableCell>
                        {queryVars.status === 'CANCEL' && (
                          <TableCell className="normal-case">
                            {renderPolicyStatus(policy['Razão'])}
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              color="secondary"
                              title="Gerir Apólice"
                              onClick={() =>
                                navigate(
                                  RouteNamesAuth.POLICY_DETAILS.replace(
                                    ':policyNo',
                                    policy['Nº da Apólice'],
                                  ),
                                )
                              }
                            >
                              Gerir Apólice
                            </Button>
                            <Button
                              variant="outline"
                              color="secondary"
                              title="Recibos"
                              onClick={() =>
                                navigate(
                                  RouteNamesAuth.INVOICES +
                                    `?policyNo=` +
                                    policy['Nº da Apólice'],
                                )
                              }
                            >
                              Recibos
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </Table>

            <div className="pt-10 text-center">
              <nav className="inline-flex justify-center gap-4  bg-white rounded-full">
                <Button
                  variant="softBg"
                  color="secondary"
                  disabled={queryVars.page === 0}
                  className="rounded-r-none"
                  onClick={() =>
                    setQueryVars(prev => ({
                      ...prev,
                      page: prev.page > 0 ? prev.page - 1 : 0,
                    }))
                  }
                >
                  Anterior
                </Button>
                <div className="text-body-4 text-metal-500 flex items-center">
                  Página {queryVars.page + 1}
                </div>
                <Button
                  variant="softBg"
                  color="secondary"
                  disabled={
                    totalCount !== null
                      ? (queryVars.page + 1) * 50 >= totalCount
                      : false
                  }
                  className="rounded-l-none"
                  onClick={() =>
                    setQueryVars(prev => ({
                      ...prev,
                      page: prev.page + 1,
                    }))
                  }
                >
                  Seguinte
                </Button>
              </nav>
            </div>
          </>
        )}
      </Wrapper>
    </div>
  )
}
